import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { ToastController } from "@ionic/angular";
import { FfModalService } from "src/app/shared/core/ff-modal.service";
import { catchError } from "rxjs/operators";
import { of, throwError } from "rxjs";
import { AccountService } from "src/app/account/account.service";
import { environment } from "src/environments/environment";
import { AuthenticationService } from "./authentication-service";
import { UpgradeSubscriptionComponent } from "src/app/payments/upgrade-subscription/upgrade-subscription.component";
import { OfflineModeService } from "../services/offline-mode.service";
import { SUPPRESS_ERROR_TOAST } from "../helpers/http-context";
import { Router } from "@angular/router";

// Public routes that don't require authentication
const publicRoutes = ["/welcome", "/slides", "/error"];

export const ErrorInterceptor: HttpInterceptorFn = (req, next) => {
  // Chat owns its messages and privacy boundary, while expired sessions still sign out.
  if (req.url.startsWith(`${environment.FLOCKFINDER_API_URL}/api/v1/assistant/`)) {
    const auth = inject(AuthenticationService);
    const router = inject(Router);
    return next(req).pipe(catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && error.error?.code !== "email_unverified" && !isPublicRoute(router.url)) {
        void auth.SignOut();
      }
      return throwError(() => error);
    }));
  }
  const toastController = inject(ToastController);
  const accountService = inject(AccountService);
  const authService = inject(AuthenticationService);
  const modalCtrl = inject(FfModalService);
  const offlineModeService = inject(OfflineModeService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Prevent infinite loops on error reporting failures
      if (req.url.includes("send-error-report")) {
        console.error("Failed to send error report", error);

        // Use simplified error handling for critical failures
        console.log("Error interceptor: Critical failure detected, showing generic error");
        showErrorToast("Service temporarily unavailable - please try again later", toastController, "warning", 5000);
        return throwError(() => error);
      }

      let errorMsg = "";

      // The caller shows this failure itself, beside the action that caused it. Adding "Error: "
      // in front of copy written for a keeper would say the same thing twice, worse. The paid-tier
      // modal and the production error report below are not toasts and still run.
      const handledByCaller = req.context.get(SUPPRESS_ERROR_TOAST);

      // Check if we're offline and handle network errors gracefully
      if (!offlineModeService.isOnlineValue && (error.status === 0 || error.status === 404 || error.status === 500)) {
        // Don't show error toasts for network errors when offline
        console.log("Network error while offline - suppressing error toast");
        return throwError(() => error);
      }

      // client-side error
      if (error.error instanceof ErrorEvent) {
        errorMsg = `Error: ${error.error.message}`;
        if (!handledByCaller) {
          showErrorToast(errorMsg, toastController);
        }
      }
      // server-side error
      else {
        if (error.status === 500 && environment.production) {
          // Simplified error reporting - just try once and handle failures gracefully
          accountService
            .sendErrorReport(error.message)
            .pipe(
              catchError(reportError => {
                console.error("Failed to send error report", reportError);

                // Show generic error message instead of signing out immediately
                showErrorToast(
                  "Service temporarily unavailable - please try again later",
                  toastController,
                  "warning",
                  5000,
                );
                return of(null);
              }),
            )
            .subscribe();
        } else if (error.status === 402) {
          // Payment required - existing logic
          const errorMessage =
            error.error?.detail || "Your subscription has expired, please upgrade your subscription.";
          showUpgradeSubscription(errorMessage, modalCtrl);
        } else {
          const _error = error.error?.detail || error.error?.error || error.message || error;
          errorMsg = `Error: ${_error}`;
          if (_error.toLowerCase().includes("token")) {
            // Check if we're on a public route before calling SignOut
            const currentUrl = router.url;
            if (!isPublicRoute(currentUrl)) {
              // Add some delay before signing out to prevent rapid cycling
              setTimeout(() => {
                authService.SignOut();
              }, 1000);
              errorMsg += " - please login again";
            }
          }
          if (!handledByCaller) {
            showErrorToast(errorMsg, toastController);
          }
        }
      }
      return throwError(() => error);
    }),
  );
};

// Helper functions
function isPublicRoute(url: string): boolean {
  return publicRoutes.some(route => url.startsWith(route));
}

async function showErrorToast(
  errorMessage: string,
  toastController: ToastController,
  color: string = "tertiary",
  duration: number = 4000,
) {
  const toast = await toastController.create({
    message: errorMessage,
    position: "top",
    duration: duration,
    color: color,
  });
  toast.present();
}

async function showUpgradeSubscription(errorMessage: string, modalCtrl: FfModalService) {
  const modal = await modalCtrl.create({
    component: UpgradeSubscriptionComponent,
    componentProps: {
      errorMessage: errorMessage,
    },
  });
  await modal.present();
}
